import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PhoneButton from '@/components/base/PhoneButton';
import { getBolgeBySlug } from '@/mocks/bolge-data';

const services = [
  { icon: 'ri-door-open-line', title: 'Acil Kapı Açma', desc: 'Kilitli kalan kapıları hasarsız açıyoruz.' },
  { icon: 'ri-lock-2-line', title: 'Kilit Değiştirme', desc: 'Güvenli, modern kilitlerle değişim.' },
  { icon: 'ri-shield-keyhole-line', title: 'Çelik Kapı Açma', desc: 'Tüm marka çelik kapılarda uzman açma.' },
  { icon: 'ri-car-line', title: 'Oto Çilingir', desc: 'Araç kapısı açma ve anahtar kopyalama.' },
  { icon: 'ri-safe-line', title: 'Kasa Açma', desc: 'Şifreli ve anahtarlı kasa açma hizmeti.' },
  { icon: 'ri-key-2-line', title: 'Anahtar Kopyalama', desc: 'Her türlü anahtar kopyalama ve yedek yapımı.' },
];

const faqs = (bolgeName: string) => [
  {
    q: `${bolgeName}'de çilingir kaç dakikada gelir?`,
    a: `Alfa Çilingir olarak ${bolgeName} bölgesinde çağrı aldıktan sonra ortalama 15 dakika içinde adresinizde oluyoruz. Trafik durumuna göre bu süre değişebilir ancak en hızlı şekilde ulaşmayı hedefliyoruz.`,
  },
  {
    q: `${bolgeName}'de gece çilingir hizmeti var mı?`,
    a: `Evet! Alfa Çilingir ${bolgeName}'de 7/24, 365 gün kesintisiz hizmet vermektedir. Gece yarısı, hafta sonu veya resmi tatillerde de hemen arayabilirsiniz.`,
  },
  {
    q: `${bolgeName} çilingir fiyatları ne kadar?`,
    a: `Fiyatlarımız hizmet türüne ve saate göre değişmektedir. İşlem öncesinde net fiyat bilgisi veriyoruz, gizli ücret yoktur. Fiyat öğrenmek için hemen arayın: 0541 624 79 82`,
  },
  {
    q: `Çelik kapı açma ${bolgeName}'de yapılıyor mu?`,
    a: `Evet, ${bolgeName}'deki tüm marka ve model çelik kapılarda hasarsız açma hizmeti veriyoruz. Uzman ekibimiz özel ekipmanlarla kapınıza zarar vermeden açar.`,
  },
];

export default function BolgeLandingPage() {
  const { bolgeSlug } = useParams<{ bolgeSlug: string }>();
  const bolge = getBolgeBySlug(bolgeSlug ?? '');

  useEffect(() => {
    if (bolge) {
      document.title = bolge.metaTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', bolge.metaDesc);

      // Canonical
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';
      canonical.href = `${siteUrl}/${bolge.slug}`;

      const faqList = faqs(bolge.name);

      // Schema.org JSON-LD — LocalBusiness + BreadcrumbList
      const existingScript = document.getElementById('schema-bolge');
      if (existingScript) existingScript.remove();

      const schema = {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'LocalBusiness',
            '@id': `${siteUrl}/${bolge.slug}#localbusiness`,
            name: 'Alfa Çilingir',
            url: `${siteUrl}/${bolge.slug}`,
            telephone: '+905416247982',
            image: bolge.img,
            description: bolge.metaDesc,
            address: {
              '@type': 'PostalAddress',
              addressLocality: bolge.name,
              addressRegion: 'İstanbul',
              addressCountry: 'TR',
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: 41.0,
              longitude: 28.64,
            },
            openingHoursSpecification: [
              {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
                opens: '00:00',
                closes: '23:59',
              },
            ],
            areaServed: {
              '@type': 'City',
              name: bolge.name,
            },
            priceRange: '₺₺',
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
              { '@type': 'ListItem', position: 2, name: 'Hizmet Bölgeleri', item: `${siteUrl}/hizmet-bolgeleri` },
              { '@type': 'ListItem', position: 3, name: bolge.title, item: `${siteUrl}/${bolge.slug}` },
            ],
          },
        ],
      };

      const script = document.createElement('script');
      script.id = 'schema-bolge';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);

      // Separate FAQPage schema — avoids Google "repeated" warning
      const existingFaqScript = document.getElementById('schema-bolge-faq');
      if (existingFaqScript) existingFaqScript.remove();

      const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqList.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a,
          },
        })),
      };

      const faqScript = document.createElement('script');
      faqScript.id = 'schema-bolge-faq';
      faqScript.type = 'application/ld+json';
      faqScript.textContent = JSON.stringify(faqSchema);
      document.head.appendChild(faqScript);
    }

    return () => {
      const s = document.getElementById('schema-bolge');
      if (s) s.remove();
      const f = document.getElementById('schema-bolge-faq');
      if (f) f.remove();
      const c = document.querySelector('link[rel="canonical"]');
      if (c) c.remove();
    };
  }, [bolge]);

  if (!bolge) return <Navigate to="/hizmet-bolgeleri" replace />;

  const faqList = faqs(bolge.name);

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[520px] flex items-end pb-0 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={bolge.img}
              alt={`${bolge.title} - Alfa Çilingir`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/30"></div>
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-32 pb-14">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-white/60 mb-5 flex-wrap">
              <Link to="/" className="hover:text-white cursor-pointer">Ana Sayfa</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
              <Link to="/hizmet-bolgeleri" className="hover:text-white cursor-pointer">Hizmet Bölgeleri</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
              <span className="text-white">{bolge.title}</span>
            </nav>

            <div className="inline-flex items-center gap-2 border border-orange-400/60 text-orange-300 text-xs font-bold px-4 py-2 rounded-full mb-5 backdrop-blur-sm bg-white/5">
              <span className="w-2 h-2 bg-orange-400 rounded-full animate-ping absolute"></span>
              <span className="w-2 h-2 bg-orange-400 rounded-full relative"></span>
              7/24 ACİL ÇİLİNGİR
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4 leading-tight">
              {bolge.name} <span className="text-orange-400">Çilingir</span>
            </h1>
            <p className="text-gray-200 text-lg md:text-xl mb-8 max-w-xl">
              {bolge.desc} <strong className="text-white">15 dakikada yanınızda.</strong>
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Click-to-Call Ana Buton */}
              <a
                href="tel:05416247982"
                className="inline-flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-400 text-white font-extrabold px-8 py-4 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer text-lg shadow-lg shadow-orange-500/30 animate-pulse"
              >
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-phone-fill text-xl"></i></span>
                Şimdi Ara — 0541 624 79 82
              </a>
              <a
                href="#mahalleler"
                className="inline-flex items-center justify-center gap-2 border-2 border-white/60 text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer text-base"
              >
                Mahalleler
                <span className="w-5 h-5 flex items-center justify-center"><i className="ri-arrow-down-line"></i></span>
              </a>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-orange-500 py-5">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { val: '15 Dk', lbl: 'Ortalama Varış' },
                { val: '7/24', lbl: 'Kesintisiz Hizmet' },
                { val: '5000+', lbl: 'Mutlu Müşteri' },
                { val: '10+', lbl: 'Yıllık Deneyim' },
              ].map((s) => (
                <div key={s.lbl}>
                  <p className="text-2xl font-extrabold text-white">{s.val}</p>
                  <p className="text-orange-100 text-xs">{s.lbl}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About + Services */}
        <section className="py-16 md:py-20 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col lg:flex-row gap-12">
              {/* Left: About */}
              <div className="lg:w-2/5">
                <span className="inline-flex items-center gap-2 text-orange-500 text-xs font-bold uppercase tracking-widest mb-3">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-map-pin-line"></i></span>
                  {bolge.name} Hakkında
                </span>
                <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
                  {bolge.name}'de Güvenilir Çilingir
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">{bolge.longDesc}</p>
                <div className="space-y-3">
                  {[
                    'Güvenilir ve deneyimli çilingirler',
                    'Hasarsız kapı açma garantisi',
                    'İşlem öncesi şeffaf fiyat',
                    'Gece gündüz, tatil dahil hizmet',
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-sm text-gray-700">
                      <span className="w-5 h-5 flex items-center justify-center text-orange-500">
                        <i className="ri-check-double-line"></i>
                      </span>
                      {item}
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:05416247982"
                    className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-extrabold px-6 py-3 rounded-full whitespace-nowrap transition-all duration-200 cursor-pointer text-base"
                  >
                    <span className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill"></i></span>
                    Şimdi Ara
                  </a>
                  <PhoneButton size="md" />
                </div>
              </div>

              {/* Right: Services Grid */}
              <div className="lg:w-3/5">
                <h2 className="text-xl font-extrabold text-gray-900 mb-5">
                  {bolge.name}'de Verdiğimiz Hizmetler
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {services.map((svc) => (
                    <div key={svc.title} className="bg-white rounded-xl p-4 border border-stone-100 flex items-start gap-3 hover:border-orange-200 transition-all">
                      <span className="w-10 h-10 flex items-center justify-center bg-orange-50 text-orange-500 rounded-lg flex-shrink-0">
                        <i className={`${svc.icon} text-lg`}></i>
                      </span>
                      <div>
                        <h3 className="font-bold text-gray-900 text-sm">{svc.title}</h3>
                        <p className="text-gray-400 text-xs mt-0.5">{svc.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mahalleler */}
        <section id="mahalleler" className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 text-orange-500 text-xs font-bold uppercase tracking-widest mb-3">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-community-line"></i></span>
                Mahalleler
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                {bolge.name} Çilingir Hizmeti Verdiğimiz Mahalleler
              </h2>
              <p className="text-gray-400 text-sm mt-2">Her mahalleye tıklayarak detaylı bilgi alabilirsiniz.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {bolge.mahalleler.map((m) => (
                <Link
                  key={m.slug}
                  to={`/${bolge.slug}/${m.slug}`}
                  className="group bg-stone-50 hover:bg-orange-50 border border-stone-100 hover:border-orange-300 rounded-xl p-4 flex items-center justify-between transition-all duration-200 cursor-pointer"
                >
                  <div>
                    <p className="font-semibold text-gray-800 text-sm group-hover:text-orange-600 transition-colors">{m.name}</p>
                    <p className="text-gray-400 text-xs mt-0.5">Çilingir</p>
                  </div>
                  <span className="w-6 h-6 flex items-center justify-center text-gray-300 group-hover:text-orange-400 transition-colors">
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="py-10 bg-stone-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-xl font-extrabold text-gray-900 mb-5 text-center">{bolge.name} Hizmet Bölgesi Haritası</h2>
            <div className="rounded-2xl overflow-hidden border border-stone-100 h-80">
              <iframe
                title={`${bolge.name} Çilingir Harita`}
                src={bolge.mapEmbed}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 text-orange-500 text-xs font-bold uppercase tracking-widest mb-3">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-question-line"></i></span>
                Sık Sorulan Sorular
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">
                {bolge.name} Çilingir Hakkında Merak Edilenler
              </h2>
            </div>
            <div className="space-y-4">
              {faqList.map((faq, i) => (
                <FaqItem key={i} q={faq.q} a={faq.a} />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-orange-500">
          <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
              {bolge.name}'de Acil Çilingir mi Lazım?
            </h2>
            <p className="text-orange-100 mb-6">7/24 hizmetinizdeyiz. Hemen arayın, 15 dakikada yanınızda olalım.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:05416247982"
                className="inline-flex items-center gap-3 bg-white text-orange-500 font-extrabold px-10 py-5 rounded-full whitespace-nowrap cursor-pointer hover:bg-orange-50 transition-all duration-200 text-xl"
              >
                <span className="w-7 h-7 flex items-center justify-center"><i className="ri-phone-fill text-2xl"></i></span>
                Şimdi Ara — 0541 624 79 82
              </a>
              <a
                href="https://wa.me/905416247982"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white font-extrabold px-8 py-5 rounded-full whitespace-nowrap cursor-pointer transition-all duration-200 text-base"
              >
                <span className="w-6 h-6 flex items-center justify-center"><i className="ri-whatsapp-line text-xl"></i></span>
                WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-stone-100 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-stone-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 text-sm pr-4">{q}</span>
        <span className={`w-6 h-6 flex items-center justify-center text-orange-500 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-xl"></i>
        </span>
      </button>
      {open && (
        <div className="px-5 pb-4 text-gray-500 text-sm leading-relaxed border-t border-stone-50">
          <p className="pt-3">{a}</p>
        </div>
      )}
    </div>
  );
}